import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Heart } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RsvpForm() {
  const [name, setName] = useState("");
  const [attending, setAttending] = useState(null);
  const [guests, setGuests] = useState("1");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || attending === null) {
      setError("გთხოვთ შეავსოთ ყველა ველი");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/rsvp`, {
        name: name.trim(),
        attending,
        guests: Number(guests),
      });
      toast.success("მადლობა! თქვენი პასუხი მიღებულია");
      setSubmitted(true);
    } catch {
      toast.error("დაფიქსირდა შეცდომა, სცადეთ თავიდან");
    } finally {
      setSubmitting(false);
    }
  };

  const optionClass = (active) =>
    `w-full rounded-lg border px-4 py-3 text-left text-sm transition-all duration-200 ${
      active
        ? "border-[#D4AF37] bg-[#FFF9EA] font-semibold text-[#8C6515] shadow-sm"
        : "border-[#E7E0D0] bg-[#FFFDF9] text-[#57534E] hover:border-[#D9C9A3]"
    }`;

  return (
    <section data-testid="rsvp-form-container" className="text-center">
      <p className="eyebrow mb-2">R.S.V.P.</p>
      <h2 className="text-xl font-medium tracking-wide text-[#292524] sm:text-2xl">
        დასწრების დადასტურება
      </h2>
      <p className="mt-2 text-sm font-light text-[#78716C]">
        გთხოვთ გვაცნობოთ თქვენი გადაწყვეტილება
      </p>

      {submitted ? (
        <div
          data-testid="rsvp-success-alert"
          className="gold-frame mx-auto mt-7 max-w-sm bg-[#FFFDF7] p-8"
        >
          <span className="btn-gold mx-auto flex h-12 w-12 items-center justify-center rounded-full">
            <Check size={20} />
          </span>
          <p className="mt-4 text-base font-medium text-[#292524]">
            მადლობა! თქვენი პასუხი მიღებულია
          </p>
          <p className="mt-1 flex items-center justify-center gap-1.5 text-sm text-[#78716C]">
            {name.trim()} <Heart size={12} className="text-[#D4AF37]" />
          </p>
          <button
            data-testid="rsvp-new-response-button"
            onClick={() => {
              setSubmitted(false);
              setName("");
              setAttending(null);
              setGuests("1");
            }}
            className="mt-5 text-xs uppercase tracking-[0.2em] text-[#B8860B] underline-offset-4 hover:underline"
          >
            ახალი პასუხი
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mx-auto mt-7 max-w-sm space-y-5 text-left">
          <div>
            <label htmlFor="rsvp-name" className="eyebrow mb-1.5 block !text-[10px]">
              სახელი და გვარი
            </label>
            <input
              id="rsvp-name"
              data-testid="rsvp-guest-name-input"
              className="input-elegant"
              placeholder="მაგ: ანა ბერიძე"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
            />
          </div>

          <div>
            <p className="eyebrow mb-1.5 !text-[10px]">დაესწრებით თუ არა?</p>
            <div data-testid="rsvp-attendance-options" className="grid grid-cols-1 gap-2.5">
              <button
                type="button"
                data-testid="rsvp-attendance-option-yes"
                className={optionClass(attending === true)}
                onClick={() => setAttending(true)}
              >
                დიახ, მოვდივარ
              </button>
              <button
                type="button"
                data-testid="rsvp-attendance-option-no"
                className={optionClass(attending === false)}
                onClick={() => setAttending(false)}
              >
                სამწუხაროდ, ვერ მოვდივარ
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="rsvp-guests" className="eyebrow mb-1.5 block !text-[10px]">
              სტუმრების რაოდენობა
            </label>
            <select
              id="rsvp-guests"
              data-testid="rsvp-guest-count-select"
              className="input-elegant appearance-none"
              value={guests}
              onChange={(e) => setGuests(e.target.value)}
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  {n} სტუმარი
                </option>
              ))}
            </select>
          </div>

          {error && (
            <p data-testid="rsvp-error-message" className="text-center text-sm text-[#B91C1C]">
              {error}
            </p>
          )}

          <button
            data-testid="rsvp-submit-button"
            type="submit"
            disabled={submitting}
            className="btn-gold w-full rounded-full py-3.5 text-sm font-semibold uppercase tracking-[0.15em] disabled:opacity-60"
          >
            {submitting ? "იგზავნება..." : "დადასტურება"}
          </button>
        </form>
      )}
    </section>
  );
}
